import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/i18n/config";
import { buildLocalizedPath, stripLanguagePrefix } from "@/i18n/useLocalizedPath";
import { SITE } from "@/config/site.js";

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  type?: string;
  /**
   * Canonical path, when the page's own URL does NOT follow from the active
   * interface language. A blog post lives at exactly one URL — the one for
   * the language its body is written in — no matter which language the
   * reader has selected.
   */
  canonicalPath?: string;
  /**
   * Explicit { lang: path } alternates. Pass `{}` for a page that exists in
   * only one language: it then declares NO hreflang, instead of claiming a
   * translation that was never written. Omit to mirror the path across every
   * supported language (correct for the translated chrome pages).
   */
  alternates?: Partial<Record<SupportedLanguage, string>>;
}

const SEOHead = ({
  title,
  description,
  path,
  keywords,
  type = "website",
  canonicalPath: canonicalOverride,
  alternates,
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const active: SupportedLanguage = isSupportedLanguage(i18n.language)
    ? i18n.language
    : DEFAULT_LANGUAGE;

  const canonicalEnglishPath = stripLanguagePrefix(path);
  const canonicalPath = canonicalOverride ?? buildLocalizedPath(canonicalEnglishPath, active);
  const url = `${SITE}${canonicalPath}`;
  const fullTitle = `${title} | Workloop`;
  // Default: this page exists in every supported language at the mirrored
  // path. A caller that knows better (a single-language article) passes its
  // own map — possibly empty.
  const alternateMap: Partial<Record<SupportedLanguage, string>> =
    alternates ??
    Object.fromEntries(
      SUPPORTED_LANGUAGES.map((lng) => [lng, buildLocalizedPath(canonicalEnglishPath, lng)]),
    );
  const alternateLangs = Object.keys(alternateMap) as SupportedLanguage[];
  // A lone language is not an alternate set — emit nothing rather than a
  // self-referential hreflang. react-helmet-async chokes on `false` children,
  // so build the tags as a (possibly empty) array.
  const alternateTags =
    alternateLangs.length > 1
      ? [
          ...alternateLangs.map((lng) => (
            <link key={lng} rel="alternate" hrefLang={lng} href={`${SITE}${alternateMap[lng]}`} />
          )),
          <link
            key="x-default"
            rel="alternate"
            hrefLang="x-default"
            href={`${SITE}${alternateMap[DEFAULT_LANGUAGE] ?? alternateMap[alternateLangs[0]]}`}
          />,
        ]
      : [];

  return (
    <Helmet>
      <html lang={active} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {alternateTags}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Workloop" />
      <meta property="og:locale" content={active === "es" ? "es_ES" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
};

export default SEOHead;
